import { ThemeEvents } from '@theme/events';

class VideoSliderSection {
  /** @param {HTMLElement} section */
  constructor(section) {
    this.section = section;
    section.addEventListener('click', this.#handleClick, true);
    section.addEventListener(ThemeEvents.mediaStartedPlaying, this.#handleMediaStarted);
    section.querySelectorAll('.video-thumb').forEach((thumb) => {
      this.#observeThumb(/** @type {HTMLElement} */ (thumb));
    });
  }

  /** @param {HTMLElement} thumb */
  #getMedia(thumb) {
    return thumb.querySelector('deferred-media');
  }

  /** @param {HTMLElement} thumb */
  #getOverlay(thumb) {
    return thumb.querySelector('.video-slider__play-overlay');
  }

  /** @param {HTMLElement} media */
  #getComponent(media) {
    return /** @type {{ showDeferredMedia?: () => void; playMedia?: () => void; pauseMedia?: () => void }} */ (
      /** @type {unknown} */ (media)
    );
  }

  /** @param {HTMLElement} thumb */
  #getThumbnail(thumb) {
    return thumb.querySelector('.video-slider__thumbnail');
  }

  /** @param {HTMLElement} thumb */
  #observeThumb(thumb) {
    if (thumb.dataset.videoSliderObserved) return;
    thumb.dataset.videoSliderObserved = 'true';

    const media = this.#getMedia(thumb);
    if (!(media instanceof HTMLElement)) return;

    const bindVideo = () => {
      const video = media.querySelector('video');
      if (!(video instanceof HTMLVideoElement) || video.dataset.videoSliderBound) return;

      video.dataset.videoSliderBound = 'true';
      video.addEventListener('play', () => this.#onPlay(thumb));
      video.addEventListener('pause', () => this.#onPause(thumb));
      video.addEventListener('ended', () => this.#onPause(thumb));
    };

    bindVideo();
    new MutationObserver(bindVideo).observe(media, { childList: true, subtree: true });
  }

  /** @param {Event} event */
  #handleMediaStarted = (event) => {
    const resource = /** @type {CustomEvent} */ (event).detail?.resource;
    if (!(resource instanceof HTMLElement)) return;

    const thumb = resource.closest('.video-thumb');
    if (!(thumb instanceof HTMLElement) || !this.section.contains(thumb)) return;

    requestAnimationFrame(() => this.#onPlay(thumb));
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const thumb = target.closest('.video-thumb');
    if (!(thumb instanceof HTMLElement) || !this.section.contains(thumb)) return;

    const media = this.#getMedia(thumb);
    if (!(media instanceof HTMLElement)) return;

    const overlay = this.#getOverlay(thumb);
    const component = this.#getComponent(media);

    if (overlay?.contains(target)) {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (!media.hasAttribute('data-media-loaded')) {
        component.showDeferredMedia?.();
      } else {
        component.playMedia?.();
      }

      this.#onPlay(thumb);
      return;
    }

    if (!thumb.classList.contains('is-playing')) return;

    event.preventDefault();
    component.pauseMedia?.();
    this.#onPause(thumb);
  };

  /** @param {HTMLElement} thumb */
  #onPlay(thumb) {
    thumb.classList.add('is-playing');
    thumb.dataset.videoLoaded = 'true';
    this.#getThumbnail(thumb)?.classList.add('is-hidden');
    this.#getOverlay(thumb)?.classList.add('is-hidden');
  }

  /** @param {HTMLElement} thumb */
  #onPause(thumb) {
    thumb.classList.remove('is-playing');
    this.#getThumbnail(thumb)?.classList.remove('is-hidden');
    this.#getOverlay(thumb)?.classList.remove('is-hidden');
  }
}

function initVideoSliders() {
  document.querySelectorAll('.video-slider-section').forEach((section) => {
    if (!(section instanceof HTMLElement) || section.dataset.videoSliderInit) return;
    section.dataset.videoSliderInit = 'true';
    new VideoSliderSection(section);
  });
}

initVideoSliders();

document.addEventListener('shopify:section:load', initVideoSliders);
